import React, { useCallback, useEffect, useMemo, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { Play } from '@lyearn/icons';
import {
  Button,
  DeprecatedIconButton,
  DividerSeparator,
  LinkButton,
  Stack,
  Tooltip,
  useSnackbar,
} from '@lyearn/ui';
import { DividerProps } from '@lyearn/ui';
import Breadcrumbs from '@/components/Breadcrumbs';
import PublishChangesPopover from '@/components/EditorBuilder/Settings/Dialogs/PublishChanges/PublishChangesPopover';
import usePageLayout from '@/components/PageLayout/usePageLayout';
import WorkspaceAccessControlDialog from '@/components/WorkspaceAccessControl/components/WorkspaceAccessControlDialog';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useIsPublishable from '@/modules/content/hooks/useIsPublishable';
import renderImage from '@/modules/content/utils/renderImage';
import ContentEditorSettings from '@/modules/corporate/components/ContentEditorSettings';
import StatusLabel from '@/modules/corporate/modules/admin/views/LearningContent/components/ListItemCard/StatusLabel';
import { useEmbeddedQuizEditor } from '@/modules/corporate/modules/course/components/QuizCardContainer/components/QuizEditorModal/context';
import { useSyncLocalizedContentMutation } from '@/modules/corporate/modules/course/graphql/mutation/generated/syncLocalizedContent';
import { useVerifyLocalizedContentMutation } from '@/modules/corporate/modules/course/graphql/mutation/generated/verifyLocalizedContent';
import { useLocalizedContentsQuery } from '@/modules/corporate/modules/course/graphql/queries/generated/localizedContents';
import LanguageSelector from '@/modules/corporate/modules/course/views/CourseBuilder/components/LanguageSelector';
import QuizExportDialog from '@/modules/corporate/modules/quiz/components/QuizExportDialog';
import useQuizExportAction from '@/modules/corporate/modules/quiz/hooks/useQuizExportAction';
import routes from '@/pages/corporate/routes';
import {
  QuizContentType,
  ResourceStatusEnum,
  ResourceTypeEnum,
  WorkspaceActions,
  WorkspaceSubjects,
} from '@/types/schema';

import useQuizLanguageInfo from '../../hooks/useQuizLanguageInfo';
import SavingState from './components/SavingState';
import { QuizEditorHeaderProps } from './types';
import { getBreadcrumbs, getEmbeddedBreadcrumbs } from './utils';

const DIVIDER_PROPS: DividerProps = { className: 'h-28 bg-bg-tertiary', orientation: 'vertical' };

function QuizEditorHeader(props: QuizEditorHeaderProps) {
  const { t } = useTranslation(['Course']);
  const {
    saving,
    quiz,
    onPublish: _onPublish,
    onRevertSuccess,
    showPublishPopover,
    onPublishSubmit,
    onPreview,
    onClose,
    refetchQuiz,
  } = props;
  const embeddedEditor = useEmbeddedQuizEditor();

  const {
    data: quizLanguageInfo,
    refetch: refetchQuizLanguageInfo,
    jobStatus,
  } = useQuizLanguageInfo({
    contentId: quiz.id,
  });

  const title = quiz.name;
  const { fetchIsPublishable } = useIsPublishable(quiz.id);
  const [{ fetching: isVerifying }, verifyLocalizedContent] = useVerifyLocalizedContentMutation();
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const [isPublishing, { on, off }] = useToggle(false);
  const [allowMinorVersionPublish, setAllowMinorVersionPublish] = useState(
    quiz.allowMinorVersionPublish,
  );
  const { [FeatureFlags.EnableMultiLanguageContent]: isMultiLanguageContentEnabled } =
    useFeatureFlags();
  const { isLocalizedContent, isSyncedContentVerified, status, id } = quiz;
  const [canBeVerified, setCanBeVerified] = useState(
    isLocalizedContent && isSyncedContentVerified === false,
  );
  const localizedContentIds = quiz?.languageConfigs?.map((config) => config.contentId) || [];

  const [localizedContents, refetchLocalizedContent] = useLocalizedContentsQuery({
    variables: { filters: { ids: localizedContentIds } },
    pause: !localizedContentIds?.length,
    requestPolicy: 'network-only',
  });
  const [{ fetching: isSyncing }, syncLocalizedContent] = useSyncLocalizedContentMutation();
  const [disableVerified, setDisableVerified] = useState(
    isLocalizedContent && isSyncedContentVerified === true,
  );
  const { menuItems, onMenuItemAction, showExportDialog, onCloseExportDialog } =
    useQuizExportAction();
  const isMainContentInDraftState = !isLocalizedContent && status == ResourceStatusEnum.Draft;
  useEffect(() => {
    if (isPublishing && !saving) {
      off();
    }
  }, [isPublishing, off, saving]);

  const onPublish = useCallback(async () => {
    refetchLocalizedContent?.();
    on();
    const result = await fetchIsPublishable();
    if (result) {
      setAllowMinorVersionPublish((prev) => result?.allowMinorVersionPublish ?? prev);
    }
    _onPublish();
  }, [_onPublish, fetchIsPublishable, on, refetchLocalizedContent]);

  const allBlocksNotTranslated =
    localizedContentIds?.length > 0 &&
    !localizedContents?.data?.contents?.edges
      ?.filter((edge: any) => edge.node?.status !== ResourceStatusEnum.Draft)
      ?.every((edge) => (edge?.node as QuizContentType)?.isSyncedContentVerified) &&
    !isLocalizedContent;

  const isMainContentPublishDisabled = !isLocalizedContent && allBlocksNotTranslated;
  useEffect(() => {
    if (isMainContentPublishDisabled && showPublishPopover) {
      onClose();
    }
  }, [isMainContentPublishDisabled, showPublishPopover, onClose]);

  const showSyncNowButton = useMemo(() => {
    if (isLocalizedContent && quizLanguageInfo) {
      // @ts-ignore
      const lastContentEditTimeAt = quizLanguageInfo.sourceContent?.lastContentEditTimeAt;
      const { sourceContentSyncedAt } = quizLanguageInfo;
      const isSynced =
        lastContentEditTimeAt &&
        sourceContentSyncedAt &&
        new Date(sourceContentSyncedAt as string).getTime() >
          new Date(lastContentEditTimeAt as string).getTime();
      return !isSynced;
    }
    return false;
  }, [quizLanguageInfo, isLocalizedContent]);

  const onVerifyClick = useCallback(async () => {
    await verifyLocalizedContent({
      data: {
        entityId: quiz.id,
      },
    }).then((result) => {
      if (result.data?.verifyLocalizedContent.success) {
        setCanBeVerified(false);
        setDisableVerified(true);
        showSnackbar({
          message: t('Course:LocalizedContentVerified'),
        });
      } else {
        showErrorSnackbar({
          message: t('Common:SomethingWentWrong'),
        });
      }
    });
  }, [
    quiz.id,
    showErrorSnackbar,
    showSnackbar,
    t,
    verifyLocalizedContent,
    setDisableVerified,
    setCanBeVerified,
  ]);

  const onSync = useCallback(async () => {
    const { sourceContent, id } = quiz;
    await syncLocalizedContent({
      data: {
        entityId: (sourceContent as QuizContentType)?.id,
        localizedEntityId: id,
      },
    }).then((res) => {
      if (res.data?.syncLocalizedContent.success) {
        refetchQuizLanguageInfo?.();
        showSnackbar({
          message: t('Course:SyncLocalizedContentSuccess'),
        });
      } else {
        showErrorSnackbar({ message: t('Common:Error.SomeErrorOccurredTryAgain') });
      }
    });
  }, [quiz, syncLocalizedContent, showSnackbar, t, showErrorSnackbar, refetchQuizLanguageInfo]);

  const renderActions = useCallback(() => {
    if (isLocalizedContent && status !== ResourceStatusEnum.Draft) {
      return (
        <Button
          className="h-40 me-8"
          data-id="submit"
          disabled={disableVerified || !canBeVerified || isSyncing}
          loading={isVerifying || isSyncing}
          size="small"
          variant="filled"
          onClick={showSyncNowButton ? onSync : onVerifyClick}>
          {showSyncNowButton ? t('Course:SyncNow') : t('Common:Actions.Verify')}
        </Button>
      );
    }
    const ActionButton = (
      <Button
        disabled={(isPublishing && saving) || allBlocksNotTranslated}
        loading={isPublishing}
        onClick={onPublish}>
        {t('Common:Actions.Publish')}
      </Button>
    );

    return (
      <PublishChangesPopover
        allBlocksNotTranslated={allBlocksNotTranslated}
        allowMinorVersionPublish={allowMinorVersionPublish}
        contentType={ResourceTypeEnum.Quiz}
        disabled={isPublishing}
        id={quiz.id}
        open={!!showPublishPopover}
        onClose={onClose}
        onPublish={onPublishSubmit}>
        {ActionButton}
      </PublishChangesPopover>
    );
  }, [
    allBlocksNotTranslated,
    allowMinorVersionPublish,
    isPublishing,
    onClose,
    onPublish,
    onPublishSubmit,
    quiz.id,
    saving,
    showPublishPopover,
    t,
    canBeVerified,
    disableVerified,
    showSyncNowButton,
    isSyncing,
    onSync,
  ]);

  const backLink = embeddedEditor
    ? undefined
    : status == ResourceStatusEnum.Draft
    ? undefined
    : routes.AdminQuizDetails(id);

  const breadcrumbs = embeddedEditor
    ? getEmbeddedBreadcrumbs({ title })
    : getBreadcrumbs({ t, title });

  usePageLayout({
    title,
    LeftNode: (
      <Stack alignItems="center" gapX={12}>
        <Breadcrumbs
          backLink={backLink}
          breadcrumbs={breadcrumbs}
          className="p-0"
          onBackClick={embeddedEditor?.onClose}
        />
        <StatusLabel status={quiz.status} />
      </Stack>
    ),
    RightNode: (
      <DividerSeparator
        alignItems="center"
        dividerProps={saving ? DIVIDER_PROPS : undefined}
        gapX={12}>
        {isMultiLanguageContentEnabled ? (
          <LanguageSelector
            className="mr-8"
            content={quizLanguageInfo}
            jobStatus={jobStatus}
            localizedContents={localizedContents?.data}
            refetchLocalizedContent={refetchLocalizedContent}
            refetchQuery={refetchQuizLanguageInfo}
          />
        ) : null}
        <SavingState isSaving={saving} />
        <Stack alignItems="center" gapX={8}>
          {embeddedEditor ? null : (
            <>
              <ContentEditorSettings
                content={quiz as QuizContentType}
                itemHeader={t('Common:ResourceTypeSM.Quiz')}
                menuItems={menuItems}
                onMenuItemAction={onMenuItemAction}
                onRevertSuccess={onRevertSuccess}
              />
              <LinkButton data-id="preview" onClick={onPreview}>
                <Tooltip title={`${t('Common:Actions.Preview')}`}>
                  <DeprecatedIconButton appearance="secondary" size="medium" variant="outlined">
                    <Play className="h-20 w-20 fill-current text-icon-active" />
                  </DeprecatedIconButton>
                </Tooltip>
              </LinkButton>
            </>
          )}
          <WorkspaceAccessControlDialog
            action={WorkspaceActions.Publish}
            fallback={<Button>{t('Common:Actions.Publish')}</Button>}
            subject={WorkspaceSubjects.Resource}>
            {renderActions()}
          </WorkspaceAccessControlDialog>
        </Stack>
      </DividerSeparator>
    ),
  });

  return showExportDialog ? (
    <QuizExportDialog
      image={renderImage({ height: 48, width: 48, image: quiz.cover?.url, contentName: quiz.name })}
      quiz={quiz}
      onClose={onCloseExportDialog}
    />
  ) : null;
}

export default QuizEditorHeader;
